import * as R from 'ramda';
import { EO_CUSTOM_EVENT } from '../../eo_mapping/datasources';
import { CORE_EVENT_NAMES, DATASOURCES_WITH_ALIASES } from '../../../../common/support/eo_mapping';
const CORE_EVENTS_BY_DATASOURCE = R.invertObj(CORE_EVENT_NAMES);
import type {
  TopNResponse,
  GroupByResponse,
  TimestampedTopNResponse,
  Filter,
  EventDescription,
  GroupByResult,
} from '../../../../main/types/druidTypes';
import { ALL_NAMED_EVENT_NAMES } from '../../eo_mapping/event_names';
import { compareAlphaNumericChars } from '../../../../main/support/utils';
import { UNDISCLOSED_EVENT_VALUE } from '../eo_constants';

type EventData = {
  total_count?: number;
  name?: string;
};
type AliasData = Record<string, Array<string>>;
export const sortCaseInsensitive = (data: Array<string>): Array<string> => {
  return data.sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase()));
};

const getEventName = (event: EventData, datasource: string) => {
  // Some core datasources may not send event names with their event firing
  // If we have a core datasource here with no event names, we want the
  // corresponding event name included in the list regardless.
  return event.name || CORE_EVENTS_BY_DATASOURCE[datasource];
};

/* ----event processing specific to GroupBy response data---- */
export const getCustomEventsFromGroupby = (data: GroupByResponse): Array<string> => {
  const customEvents: string[] = [];
  R.pathOr([], [EO_CUSTOM_EVENT], data).forEach(({ event }) => {
    if (event && event.total_count > 0) {
      customEvents.push(`${EO_CUSTOM_EVENT}-${event.name || ''}`);
    }
  });
  return customEvents;
};
export const getAllEvents = (data: GroupByResponse): Array<string> => {
  const standardEvents = sortCaseInsensitive(Object.keys(ALL_NAMED_EVENT_NAMES));
  const customEvents = sortCaseInsensitive(getCustomEventsFromGroupby(data));
  const allEvents = [...standardEvents, ...customEvents];
  // If the app has not fired any events yet, we will use the list of core event names
  // This is needed for dev and QA to test with new apps, though is an unlikely scenario
  // for a real live customer.
  return allEvents.length ? allEvents : sortCaseInsensitive(Object.keys(ALL_NAMED_EVENT_NAMES));
};
export const getAllEventAliases = (data: GroupByResponse): AliasData => {
  const datasources: Array<string> = Object.keys(data);
  return datasources.reduce((aliases: AliasData, datasource: string): AliasData => {
    if (DATASOURCES_WITH_ALIASES.includes(datasource)) {
      (R.path<GroupByResult[]>([datasource], data) || []).forEach((eventRow: GroupByResult) => {
        const { event } = eventRow;
        const totalCount = R.pathOr(0, ['total_count'], event);

        if (totalCount > 0 && event?.customer_event_alias) {
          const name = getEventName(event, datasource);

          if (!aliases[name]) {
            aliases[name] = [];
          }

          aliases[name].push(event.customer_event_alias);
        }
      });
    }

    return aliases;
  }, {});
};

/* ----Event processing specific to TopN response data---- */
const getEventsForDatasource = (data: TopNResponse, datasource: string): Array<EventData> => {
  return R.pathOr([], [datasource, '0', 'result'], data);
};

export const getCustomEvents = (data: TopNResponse): Array<string> => {
  const customEvents = getEventsForDatasource(data, EO_CUSTOM_EVENT);
  return customEvents.map((e) => `${EO_CUSTOM_EVENT}-${e.name || ''}`).sort((a, b) => compareAlphaNumericChars(a, b));
};
export function getAllTopEvents(data: TopNResponse): string[] {
  const standardEvents = sortCaseInsensitive(Object.keys(ALL_NAMED_EVENT_NAMES));
  const customEvents = sortCaseInsensitive(getTopEventNamesInCustomDatasource(data));
  const allEvents = [...standardEvents, ...customEvents];
  // If the app has not fired any events yet, we will use the list of core event names
  // This is needed for dev and QA to test with new apps, though is an unlikely scenario
  // for a real live customer.
  return allEvents.length ? allEvents : sortCaseInsensitive(Object.keys(ALL_NAMED_EVENT_NAMES));
}
export function getCustomerEventAliases(data: TimestampedTopNResponse[]): string[] {
  const customAliasEvents = getDimensionValuesFromTimestampedTopNResponses('customer_event_alias', data);
  return customAliasEvents;
}
export function getTopEventNamesInStandardDatasources(data: TopNResponse): string[] {
  const datasources = Object.keys(data);
  const eventsInDatasources = datasources.reduce((eventNames: string[], datasource: string) => {
    if (datasource === EO_CUSTOM_EVENT) {
      return eventNames;
    }

    const datasourceData = data[datasource];
    const explicitEventNamesFromDatasource = getDimensionValuesFromTimestampedTopNResponses('name', datasourceData);
    let allEventNamesFromDatasource = explicitEventNamesFromDatasource;

    if (hasCount(datasourceData)) {
      allEventNamesFromDatasource = addImplicitEventNameForDatasource(explicitEventNamesFromDatasource, datasource);
    }

    return [...allEventNamesFromDatasource, ...eventNames];
  }, []);
  return [...new Set(eventsInDatasources)];
}
export function getTopEventNamesInCustomDatasource(data: TopNResponse): string[] {
  const customDatasourceData = data[EO_CUSTOM_EVENT];

  if (!customDatasourceData) {
    return [];
  }

  const explicitEventNamesFromCustomDatasource = getDimensionValuesFromTimestampedTopNResponses(
    'name',
    customDatasourceData
  );
  const formattedEventNames = formatEventNamesForCustomDatasource(explicitEventNamesFromCustomDatasource);
  return [...new Set(formattedEventNames)];
}
export function getDimensionValuesFromTimestampedTopNResponses(
  dimension: string,
  timestampedTopNResponses: TimestampedTopNResponse[]
): string[] {
  return timestampedTopNResponses.reduce((eventNames: string[], response: TimestampedTopNResponse) => {
    const explicitEventNamesFromResponse = getDimensionValuesFromTimestampedTopNResponse(dimension, response);
    return [...eventNames, ...explicitEventNamesFromResponse];
  }, []);
}
export function getDimensionValuesFromTimestampedTopNResponse(
  dimension: string,
  timestampedTopNResponse: TimestampedTopNResponse
): string[] {
  if (!timestampedTopNResponse.result) {
    return [];
  }

  const explicitEventNames = timestampedTopNResponse.result.map((druidResult) => {
    const isCountUndisclosed =
      druidResult.total_count === UNDISCLOSED_EVENT_VALUE || druidResult.unique_count === UNDISCLOSED_EVENT_VALUE;
    const count = druidResult.total_count || druidResult.unique_count;

    if (!isCountUndisclosed && !count) {
      return;
    }

    return druidResult[dimension];
  });
  return explicitEventNames.filter((eventName): eventName is string => Boolean(eventName));
}
export function hasCount(datasourceData: TimestampedTopNResponse[]) {
  return datasourceData.some((timestampedData) => {
    return (
      timestampedData.result &&
      timestampedData.result.some((eventData) => {
        const count = eventData.total_count || eventData.unique_count;
        return count && count > 0;
      })
    );
  });
}
export function addImplicitEventNameForDatasource(explicitEventNames: string[], datasource: string): string[] {
  const implicitEventNameForDatasource = CORE_EVENTS_BY_DATASOURCE[datasource];

  if (implicitEventNameForDatasource) {
    return [...explicitEventNames, implicitEventNameForDatasource];
  }

  return explicitEventNames;
}
export function formatEventNamesForCustomDatasource(eventNames: string[]): string[] {
  return eventNames.map((eventName) => `${EO_CUSTOM_EVENT}-${eventName}`);
}
export function hasCustomerEventAliases(data: TopNResponse) {
  const datasources = Object.keys(data);
  return datasources.some((datasource) => {
    const datasourceData = data[datasource];
    const customerEventAliases = getCustomerEventAliases(datasourceData);
    return !!customerEventAliases.length;
  });
}
export function createEventDescription(options: {
  dataSource: string;
  filter?: Filter;
  displayName: string;
}): EventDescription {
  const { dataSource, filter, displayName } = options;
  return {
    dataSource,
    filter,
    displayName,
  };
}
export function parseEventDescriptionFromEventKeyOrUndefined(event: string): EventDescription | void {
  try {
    return JSON.parse(event);
  } catch (e) {
    return undefined;
  }
}
export function getLabelFromEventDescription(eventDescription: EventDescription): string {
  return eventDescription.displayName;
}
